import React from 'react';
import { Plus, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const standardColors = [
  { name: 'Rot', color: '#DC2626' },
  { name: 'Rosa', color: '#EC4899' },
  { name: 'Orange', color: '#F97316' },
  { name: 'Gelb', color: '#FBBF24' },
  { name: 'Grün', color: '#10B981' },
  { name: 'Hellgrün', color: '#84CC16' },
  { name: 'Türkis', color: '#14B8A6' },
  { name: 'Cyan', color: '#06B6D4' },
  { name: 'Blau', color: '#3B82F6' },
  { name: 'Hellblau', color: '#60A5FA' },
  { name: 'Indigo', color: '#6366F1' },
  { name: 'Lila', color: '#8B5CF6' },
  { name: 'Goldbraun', color: '#C4A084' },
  { name: 'Braun', color: '#92400E' },
  { name: 'Hellbraun', color: '#D2B48C' },
  { name: 'Beige', color: '#F5F5DC' },
  { name: 'Hellbeige', color: '#E8E0D5' },
  { name: 'Grau', color: '#6B7280' },
  { name: 'Dunkelgrau', color: '#374151' },
  { name: 'Schwarz', color: '#1F2937' },
  { name: 'Weiß', color: '#FFFFFF' },
  { name: 'Creme', color: '#FDF4E3' },
  { name: 'Ivory', color: '#FFFFF0' },
  { name: 'Sand', color: '#F4E4C1' }
];

const presetThemes = [
  {
    name: 'Goldbraun Elegant',
    colors: {
      primary: '#C4A084',
      secondary: '#B89968',
      tertiary: '#A88B5E',
      background: '#E8E0D5',
      buttonText: '#FFFFFF',
      footerBg: '#C4A084'
    }
  },
  {
    name: 'Pastell Rosa',
    colors: {
      primary: '#FFB6C1',
      secondary: '#FF69B4',
      tertiary: '#FFC0CB',
      background: '#FFF0F5',
      buttonText: '#8B7355',
      footerBg: '#FFE4E1'
    }
  },
  {
    name: 'Pastell Grün',
    colors: {
      primary: '#98D8C8',
      secondary: '#5F9EA0',
      tertiary: '#90EE90',
      background: '#F0FFF4',
      buttonText: '#2F4F4F',
      footerBg: '#E0F2F1'
    }
  },
  {
    name: 'Pastell Blau',
    colors: {
      primary: '#B0E0E6',
      secondary: '#4682B4',
      tertiary: '#87CEEB',
      background: '#F0F8FF',
      buttonText: '#1E3A5F',
      footerBg: '#E6F3FF'
    }
  },
  {
    name: 'Pastell Gelb',
    colors: {
      primary: '#F0E68C',
      secondary: '#DAA520',
      tertiary: '#FFFFE0',
      background: '#FFFACD',
      buttonText: '#8B7355',
      footerBg: '#FFF9E6'
    }
  },
  {
    name: 'Klassisch Blau',
    colors: {
      primary: '#4A90E2',
      secondary: '#357ABD',
      tertiary: '#2E6DA4',
      background: '#F5F7FA',
      buttonText: '#FFFFFF',
      footerBg: '#4A90E2'
    }
  },
  {
    name: 'Natürlich Grün',
    colors: {
      primary: '#7CB342',
      secondary: '#689F38',
      tertiary: '#558B2F',
      background: '#F1F8E9',
      buttonText: '#FFFFFF',
      footerBg: '#7CB342'
    }
  },
  {
    name: 'Rosa Elegant',
    colors: {
      primary: '#E91E63',
      secondary: '#C2185B',
      tertiary: '#AD1457',
      background: '#FCE4EC',
      buttonText: '#FFFFFF',
      footerBg: '#E91E63'
    }
  }
];

const ColorPalettesManager = ({ newTheme, setNewTheme, onSave }) => {
  const loadPreset = (preset) => {
    setNewTheme(prev => ({
      name: prev.name || preset.name,
      colors: preset.colors
    }));
    toast.success(`Farben von "${preset.name}" geladen`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Neue Farbpalette erstellen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="theme-name">Name der Vorlage</Label>
            <Input
              id="theme-name"
              value={newTheme.name}
              onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
              placeholder="z.B. Meine Farbpalette"
            />
          </div>

          {/* Standard Color Palette */}
          <div className="mb-4 p-3 md:p-4 bg-gray-50 rounded-lg">
            <Label className="mb-2 block text-sm md:text-base font-semibold">Standardfarben (Klicken zum Übernehmen)</Label>
            <div className="grid grid-cols-6 md:grid-cols-8 gap-2">
              {standardColors.map((standardColor) => (
                <button
                  key={standardColor.color}
                  type="button"
                  onClick={() => {
                    const currentField = document.activeElement.closest('[data-color-field]')?.getAttribute('data-color-field') || 'primary';
                    setNewTheme({
                      ...newTheme,
                      colors: { ...newTheme.colors, [currentField]: standardColor.color }
                    });
                    toast.success(`${standardColor.name} ausgewählt`);
                  }}
                  className="w-10 h-10 rounded border-2 border-gray-300 hover:border-gray-600 transition-colors"
                  style={{ backgroundColor: standardColor.color }}
                  title={`${standardColor.name} (${standardColor.color})`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-2">Tipp: Klicken Sie auf ein Farbfeld unten, dann auf eine Standardfarbe</p>
          </div>

          <div className="space-y-3">
            <div data-color-field="primary">
              <Label htmlFor="primary">Primärfarbe (Header, Buttons)</Label>
              <div className="flex gap-2">
                <Input
                  id="primary"
                  type="color"
                  value={newTheme.colors.primary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, primary: e.target.value }
                  })}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={newTheme.colors.primary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, primary: e.target.value }
                  })}
                  data-color-field="primary"
                />
              </div>
            </div>

            <div data-color-field="secondary">
              <Label htmlFor="secondary">Sekundärfarbe (Gradient Mitte)</Label>
              <div className="flex gap-2">
                <Input
                  id="secondary"
                  type="color"
                  value={newTheme.colors.secondary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, secondary: e.target.value }
                  })}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={newTheme.colors.secondary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, secondary: e.target.value }
                  })}
                  data-color-field="secondary"
                />
              </div>
            </div>

            <div data-color-field="tertiary">
              <Label htmlFor="tertiary">Tertiärfarbe (Gradient Ende)</Label>
              <div className="flex gap-2">
                <Input
                  id="tertiary"
                  type="color"
                  value={newTheme.colors.tertiary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, tertiary: e.target.value }
                  })}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={newTheme.colors.tertiary}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, tertiary: e.target.value }
                  })}
                  data-color-field="tertiary"
                />
              </div>
            </div>

            <div data-color-field="background">
              <Label htmlFor="background">Hintergrundfarbe</Label>
              <div className="flex gap-2">
                <Input
                  id="background"
                  type="color"
                  value={newTheme.colors.background}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, background: e.target.value }
                  })}
                  className="w-20 h-10"
                />
                <Input
                  type="text"
                  value={newTheme.colors.background}
                  onChange={(e) => setNewTheme({
                    ...newTheme,
                    colors: { ...newTheme.colors, background: e.target.value }
                  })}
                  data-color-field="background"
                />
              </div>
            </div>
          </div>

          <Button onClick={onSave} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Vorlage speichern
          </Button>
        </CardContent>
      </Card>

      {/* Preview & Presets */}
      <div className="space-y-6">
        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Vorschau
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Header Preview */}
              <div
                className="p-6 rounded-lg text-white text-center"
                style={{
                  background: `linear-gradient(135deg, ${newTheme.colors.primary} 0%, ${newTheme.colors.secondary} 50%, ${newTheme.colors.tertiary} 100%)`
                }}
              >
                <h3 className="text-xl font-bold mb-2">Homeyoursoul</h3>
                <p className="text-sm">Header Vorschau</p>
              </div>

              {/* Background Preview */}
              <div
                className="p-6 rounded-lg"
                style={{ backgroundColor: newTheme.colors.background }}
              >
                <p className="text-sm text-gray-700">Hintergrund Vorschau</p>
              </div>

              {/* Button Preview */}
              <button
                className="w-full px-4 py-2 rounded text-white font-medium"
                style={{
                  background: `linear-gradient(135deg, ${newTheme.colors.primary} 0%, ${newTheme.colors.secondary} 100%)`
                }}
              >
                Button Vorschau
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Preset Templates */}
        <Card>
          <CardHeader>
            <CardTitle>Schnellvorlagen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {presetThemes.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => loadPreset(preset)}
                  className="w-full p-3 border rounded hover:bg-gray-50 text-left flex items-center gap-3"
                >
                  <div className="flex gap-1">
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.primary }} />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.secondary }} />
                    <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.tertiary }} />
                  </div>
                  <span className="text-sm font-medium">{preset.name}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ColorPalettesManager;
